import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Eye, Envelope, Phone, Briefcase, Sparkle, CircleNotch, X, FilePdf, Funnel, MapPin, Clock } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CandidatesList = () => {
  const { token } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isAiSearchActive, setIsAiSearchActive] = useState(false);
  const [filterRole, setFilterRole] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterMinYears, setFilterMinYears] = useState('');
  const debounceTimer = useRef(null);

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!searchQuery.trim()) {
      setIsAiSearchActive(false);
      setSearchResults([]);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      performAiSearch(searchQuery);
    }, 600);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  const fetchCandidates = async () => {
    try {
      const response = await axios.get(`${API}/candidates`);
      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const performAiSearch = async (query) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setIsAiSearchActive(true);
    try {
      const response = await axios.post(`${API}/candidates/search`, { query });
      setSearchResults(response.data);
    } catch (error) {
      console.error('AI search error:', error);
      toast.error('AI search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsAiSearchActive(false);
  };

  const deleteCandidate = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this candidate?')) {
      return;
    }

    try {
      await axios.delete(`${API}/candidates/${id}`);
      toast.success('Candidate deleted successfully');
      fetchCandidates();
      if (searchQuery) {
        performAiSearch(searchQuery);
      }
    } catch (error) {
      console.error('Error deleting candidate:', error);
      toast.error('Failed to delete candidate');
    }
  };

  // Determine what to display: search results (with reasons) or all candidates
  const baseItems = isAiSearchActive
    ? searchResults
    : candidates.map((c) => ({ candidate: c, relevance_score: null, match_reason: null }));

  // Apply filters (client-side)
  const filterRoleLc = filterRole.trim().toLowerCase();
  const filterLocationLc = filterLocation.trim().toLowerCase();
  const minYears = filterMinYears !== '' ? parseFloat(filterMinYears) : null;

  const displayItems = baseItems.filter(({ candidate }) => {
    if (filterRoleLc && !(candidate.role_applied_for || '').toLowerCase().includes(filterRoleLc)) return false;
    if (filterLocationLc && !(candidate.location || '').toLowerCase().includes(filterLocationLc)) return false;
    if (minYears !== null && !isNaN(minYears)) {
      const yr = candidate.years_experience;
      if (yr === null || yr === undefined || yr < minYears) return false;
    }
    return true;
  });

  const activeFiltersCount = [filterRole, filterLocation, filterMinYears].filter(Boolean).length;
  const clearFilters = () => { setFilterRole(''); setFilterLocation(''); setFilterMinYears(''); };

  // Build unique role & location values for datalist suggestions
  const uniqueRoles = Array.from(new Set(candidates.map(c => c.role_applied_for).filter(Boolean))).sort();
  const uniqueLocations = Array.from(new Set(candidates.map(c => c.location).filter(Boolean))).sort();

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-white border border-slate-200 rounded-lg skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="candidates-list-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Candidates
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            Manage and review all candidate profiles
          </p>
        </div>

        {/* AI Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              {isSearching ? (
                <CircleNotch size={20} className="text-[#002FA7] animate-spin" />
              ) : (
                <Sparkle size={20} weight="duotone" className="text-[#002FA7]" />
              )}
            </div>
            <input
              type="text"
              placeholder='Try: "Python developers from MIT" or "Senior engineers with cloud experience"'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-12 py-3.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="ai-search-input"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                data-testid="clear-search-button"
              >
                <X size={20} />
              </button>
            )}
          </div>

          {/* AI Search Status */}
          {isAiSearchActive && (
            <div className="mt-3 flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-md" data-testid="ai-search-status">
              <Sparkle size={16} weight="fill" className="text-[#002FA7]" />
              <p className="text-sm text-slate-700">
                {isSearching ? (
                  <span>AI is analyzing candidates for "<strong>{searchQuery}</strong>"...</span>
                ) : (
                  <span>
                    AI found <strong>{searchResults.length}</strong> candidate{searchResults.length !== 1 ? 's' : ''} matching "<strong>{searchQuery}</strong>"
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Filter Bar */}
        <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4" data-testid="filter-bar">
          <div className="flex items-center space-x-2 mb-3">
            <Funnel size={16} weight="duotone" className="text-[#002FA7]" />
            <span className="text-sm font-medium text-slate-700">Filters</span>
            {activeFiltersCount > 0 && (
              <>
                <span className="text-xs font-medium bg-blue-50 text-[#002FA7] px-2 py-0.5 rounded" data-testid="filters-active-count">
                  {activeFiltersCount} active
                </span>
                <button
                  onClick={clearFilters}
                  className="text-xs text-slate-500 hover:text-red-600 transition-colors underline underline-offset-2"
                  data-testid="clear-filters-button"
                >
                  Clear all
                </button>
              </>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <Briefcase size={12} />
                <span>Role Applied For</span>
              </label>
              <input
                type="text"
                list="role-suggestions"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                placeholder="e.g. Senior Engineer"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-role-input"
              />
              <datalist id="role-suggestions">
                {uniqueRoles.map((r) => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <MapPin size={12} />
                <span>Location</span>
              </label>
              <input
                type="text"
                list="location-suggestions"
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                placeholder="e.g. Bangalore"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-location-input"
              />
              <datalist id="location-suggestions">
                {uniqueLocations.map((l) => <option key={l} value={l} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center space-x-1">
                <Clock size={12} />
                <span>Min. Years of Experience</span>
              </label>
              <input
                type="number"
                min="0"
                max="60"
                value={filterMinYears}
                onChange={(e) => setFilterMinYears(e.target.value)}
                placeholder="e.g. 5"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="filter-min-years-input"
              />
            </div>
          </div>
          {activeFiltersCount > 0 && (
            <div className="mt-3 text-xs text-slate-500" data-testid="filter-result-count">
              Showing <strong className="text-slate-800">{displayItems.length}</strong> of {baseItems.length} candidates
            </div>
          )}
        </div>

        {displayItems.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <p className="text-base text-slate-600">
              {isAiSearchActive
                ? 'No candidates found matching your query. Try different keywords or a broader search.'
                : 'No candidates yet. Start by uploading CVs.'}
            </p>
            {!isAiSearchActive && (
              <Link
                to="/upload"
                className="inline-block mt-4 btn-primary px-6 py-2 rounded-md text-sm font-medium"
              >
                Upload CVs
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="candidates-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      {isAiSearchActive ? 'AI Match' : 'Skills'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold tracking-[0.2em] uppercase text-slate-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {displayItems.map(({ candidate, relevance_score, match_reason }) => (
                    <tr
                      key={candidate.id}
                      className="candidate-row"
                      data-testid={`candidate-row-${candidate.id}`}
                    >
                      <td className="px-6 py-4">
                        <Link
                          to={`/candidates/${candidate.id}`}
                          className="text-base font-medium text-slate-900 hover:text-[#002FA7]"
                        >
                          {candidate.name || 'Unknown'}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {candidate.email && (
                            <div className="flex items-center space-x-2 text-sm text-slate-600">
                              <Envelope size={16} />
                              <span>{candidate.email}</span>
                            </div>
                          )}
                          {candidate.phone && (
                            <div className="flex items-center space-x-2 text-sm text-slate-600">
                              <Phone size={16} />
                              <span>{candidate.phone}</span>
                            </div>
                          )}
                          {candidate.location && (
                            <div className="flex items-center space-x-2 text-sm text-slate-500">
                              <MapPin size={14} />
                              <span>{candidate.location}</span>
                            </div>
                          )}
                          {candidate.years_experience !== null && candidate.years_experience !== undefined && (
                            <div className="flex items-center space-x-2 text-sm text-slate-500">
                              <Clock size={14} />
                              <span>{candidate.years_experience} yr{candidate.years_experience === 1 ? '' : 's'} exp.</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {candidate.role && (
                          <div className="flex items-center space-x-2 text-sm text-slate-700">
                            <Briefcase size={16} />
                            <span>{candidate.role}</span>
                          </div>
                        )}
                        {candidate.role_applied_for && (
                          <div className="mt-1 text-xs text-slate-500">
                            <span className="font-medium">Applied for:</span> {candidate.role_applied_for}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        {isAiSearchActive && match_reason ? (
                          <div className="space-y-1.5" data-testid={`match-reason-${candidate.id}`}>
                            <div className="flex items-center space-x-2">
                              <Sparkle size={14} weight="fill" className="text-[#002FA7]" />
                              <span className="text-xs font-semibold text-[#002FA7]">
                                {Math.round((relevance_score || 0) * 100)}% match
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed">{match_reason}</p>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {candidate.skills ? (
                              candidate.skills
                                .split(',')
                                .slice(0, 3)
                                .map((skill, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded"
                                  >
                                    {skill.trim()}
                                  </span>
                                ))
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <a
                            href={`${API}/candidates/${candidate.id}/cv?token=${encodeURIComponent(token || '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 text-slate-600 hover:text-[#002FA7] hover:bg-slate-100 rounded transition-colors"
                            title="Open CV"
                            data-testid={`open-cv-${candidate.id}`}
                          >
                            <FilePdf size={20} />
                          </a>
                          <Link
                            to={`/candidates/${candidate.id}`}
                            className="p-2 text-slate-600 hover:text-[#002FA7] hover:bg-slate-100 rounded transition-colors"
                            data-testid={`view-candidate-${candidate.id}`}
                          >
                            <Eye size={20} />
                          </Link>
                          <button
                            onClick={(e) => deleteCandidate(candidate.id, e)}
                            className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            data-testid={`delete-candidate-${candidate.id}`}
                          >
                            <Trash size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidatesList;
